import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaBullhorn, FaHistory, FaPaperPlane, FaComments } from 'react-icons/fa';
import MessageHistory from '../components/messaging/MessageHistory';
import ComposeTab from '../components/messaging/ComposeTab';
import ConversationsTab from '../components/messaging/ConversationsTab';
import { usePolling } from '../hooks/useConversations';
import { getConversations } from '../services/messagingService';

// Shell for the Messaging module. Each tab owns its own data and state; this page only routes
// between them and carries the unread badge so a reply is visible from any tab.
//   Compose       broadcast SMS/email to franchisees (tickets 4, 17)
//   Conversations two-way SMS threads with franchisees (ticket 71)
//   History       everything the system has sent (ticket 14)
//
// Email Monitor + Knowledge live in their own module (/email-monitor).

// Only fires while the Conversations tab is closed; when it is open it reports its own count, so the
// unread endpoint is never polled twice at once.
const BADGE_POLL_MS = 45_000;

const TABS = [
  { key: 'compose', label: 'Compose', Icon: FaPaperPlane },
  { key: 'conversations', label: 'Conversations', Icon: FaComments },
  { key: 'history', label: 'History', Icon: FaHistory },
];
const DEFAULT_TAB = 'compose';

// Params owned by the Conversations tab; dropped when leaving it so a stale thread cannot reopen.
const CONVERSATION_PARAMS = ['c', 'to'];

const MessagingPage = () => {
  // The URL is the single source of truth for the tab (ticket 43's direction): back/forward works,
  // a filtered view is linkable, and there is no second copy in state to fall out of sync.
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab');
  const tab = TABS.some((t) => t.key === requestedTab) ? requestedTab : DEFAULT_TAB;

  const [totalUnread, setTotalUnread] = useState(0);

  const selectTab = useCallback((next) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      // The default tab carries no param, matching how InvoicesPage keeps the canonical URL clean.
      if (next === DEFAULT_TAB) params.delete('tab');
      else params.set('tab', next);
      if (next !== 'conversations') CONVERSATION_PARAMS.forEach((key) => params.delete(key));
      return params;
    });
  }, [setSearchParams]);

  const refreshUnread = useCallback(async () => {
    try {
      const { totalUnread: count } = await getConversations();
      setTotalUnread(count);
    } catch {
      // A failed badge refresh is not worth surfacing; the tab itself reports real errors.
    }
  }, []);

  // Seed the badge on mount, and refresh it on returning from the Conversations tab.
  useEffect(() => {
    if (tab !== 'conversations') refreshUnread();
  }, [tab, refreshUnread]);

  usePolling(refreshUnread, BADGE_POLL_MS, tab !== 'conversations');

  return (
    <div className="container mx-auto px-4 py-8">
      <div className={tab === 'compose' ? 'max-w-5xl mx-auto' : 'max-w-7xl mx-auto'}>
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-jims-cyan-dark flex items-center gap-3">
            <FaBullhorn /> Messaging
          </h1>
        </div>

        <div className="border-b border-gray-200 mb-5" role="tablist" aria-label="Messaging view">
          <nav className="-mb-px flex gap-6">
            {TABS.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => selectTab(key)}
                className={`flex items-center gap-2 py-3 px-1 text-sm font-medium border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jims-cyan rounded-t ${
                  tab === key
                    ? 'border-jims-cyan text-jims-cyan-dark'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon /> {label}
                {key === 'conversations' && totalUnread > 0 && (
                  <span
                    className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-jims-cyan text-white text-xs font-bold"
                    aria-label={`${totalUnread} unread`}
                  >
                    {totalUnread > 99 ? '99+' : totalUnread}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {tab === 'compose' && <ComposeTab />}
        {tab === 'conversations' && <ConversationsTab onUnreadChange={setTotalUnread} />}
        {tab === 'history' && <MessageHistory />}
      </div>
    </div>
  );
};

export default MessagingPage;
